// highlight-html.js — UAE HTML highlighting only

export const DocumentColorEnum = {
  yellow: 'yellow',
  blue: 'blue',
  red: 'red',
  green: 'green',
  purple: 'purple'
};

export const highlightColors = {
  [DocumentColorEnum.yellow]: 'bg-llama-yellow-light',
  [DocumentColorEnum.blue]: 'bg-llama-blue-light',
  [DocumentColorEnum.red]: 'bg-llama-red-light',
  [DocumentColorEnum.green]: 'bg-llama-green-light',
  [DocumentColorEnum.purple]: 'bg-llama-purple-light'
};

const HIGHLIGHT_CLASSNAME = 'highlighted-by-llama';

const highlightStyles = `
  .highlighted-by-llama {
    background-color: rgba(255, 255, 0, 0.3) !important;
    border-radius: 4px;
    padding: 4px 2px;
    border-left: 4px solid rgba(255, 255, 0, 0.8);
    transition: all 0.3s ease;
  }
  .highlighted-by-llama.bg-llama-yellow-light {
    background-color: rgba(255, 255, 0, 0.3) !important;
    border-left-color: rgba(255, 255, 0, 0.8);
  }
  .highlighted-by-llama.bg-llama-blue-light {
    background-color: rgba(0, 123, 255, 0.3) !important;
    border-left-color: rgba(0, 123, 255, 0.8);
  }
  .highlighted-by-llama.bg-llama-red-light {
    background-color: rgba(255, 0, 0, 0.3) !important;
    border-left-color: rgba(255, 0, 0, 0.8);
  }
  .highlighted-by-llama.bg-llama-green-light {
    background-color: rgba(0, 255, 0, 0.3) !important;
    border-left-color: rgba(0, 255, 0, 0.8);
  }
  .highlighted-by-llama.bg-llama-purple-light {
    background-color: rgba(128, 0, 128, 0.3) !important;
    border-left-color: rgba(128, 0, 128, 0.8);
  }
`;

// Jurisprudence section keys that can be highlighted
const JURISPRUDENCE_SECTION_KEYS = [
  'appellant',
  'case_details',
  'case_facts',
  'court_decision',
  'court_info',
  'defendant',
  'judges_panel',
  'lawyer',
  'legal_reference',
  'other_section',
  'parties',
  'party_other',
  'plaintiff',
  'precedent',
  'reasoning_point',
  'signature_date',
  'table',
  'title',
  'witness'
];

// Track scroll state to prevent conflicts
let isAutoScrolling = false;
let autoScrollTimer = null;

function addHighlightStyles(document) {
  if (!document.getElementById('highlight-styles')) {
    const styleElement = document.createElement('style');
    styleElement.id = 'highlight-styles';
    styleElement.textContent = highlightStyles;
    document.head.appendChild(styleElement);
  }
}

function normalizeText(text) {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

export function removeExistingHighlights(document) {
  const existingHighlights = document.querySelectorAll(`.${HIGHLIGHT_CLASSNAME}`);
  existingHighlights.forEach(element => {
    element.classList.remove(...Object.values(highlightColors));
    element.classList.remove(HIGHLIGHT_CLASSNAME);
  });
}

function highlightElement(element, colorClass) {
  if (!element) return;
  element.classList.add(HIGHLIGHT_CLASSNAME, colorClass);
}

function scrollToElementOnce(element) {
  if (!element || isAutoScrolling) return;

  if (autoScrollTimer) clearTimeout(autoScrollTimer);

  isAutoScrolling = true;
  setTimeout(() => {
    try {
      element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      autoScrollTimer = setTimeout(() => { isAutoScrolling = false; }, 1500);
    } catch (error) {
      console.warn('Failed to scroll to highlighted element:', error);
      isAutoScrolling = false;
    }
  }, 100);
}

// Parse jurisprudence section title e.g. "reasoning_point_2" → { key: "reasoning_point", number: "2" }
function parseJurisprudenceSectionTitle(sectionTitle) {
  if (!sectionTitle) return null;

  const match = sectionTitle.match(/^([a-zA-Z_]+)_(\d+)$/);
  if (match) {
    const key = match[1];
    const number = match[2];
    if (JURISPRUDENCE_SECTION_KEYS.includes(key)) {
      return { key, number };
    }
  }
  return null;
}

// Find a UAE law article using data-article attribute, highlight article-content
function findUaeLawArticle(doc, sectionTitle) {
  if (!sectionTitle) return null;

  const numberMatch = sectionTitle.match(/\d+(?:[-./]\d+)*/);
  if (!numberMatch) return null;

  const articleNumber = numberMatch[0];
  const articleEl = doc.querySelector(`[data-article="${articleNumber}"]`);
  if (!articleEl) return null;

  // Prefer article-content for precise highlighting; fall back to whole article
  return articleEl.querySelector('.article-content') || articleEl;
}

// Find a UAE jurisprudence section by key and 1-based index
function findUaeJurisprudenceSection(doc, sectionKey, sectionNumber) {
  if (!sectionKey || !sectionNumber) return null;

  const cssClass = sectionKey.replace(/_/g, '-');
  const index = parseInt(sectionNumber, 10) - 1; // convert to 0-based

  // Special case: reasoning_point lives inside .legal-reasoning container
  if (sectionKey === 'reasoning_point') {
    const container = doc.querySelector('.legal-reasoning');
    if (container) {
      const points = container.querySelectorAll('.reasoning-point');
      if (index >= 0 && index < points.length) return points[index];
    }
  }

  // General case: nth element with the class (1-based → 0-based)
  const elements = doc.querySelectorAll(`.${cssClass}`);
  if (index >= 0 && index < elements.length) return elements[index];

  // Fallback: first element of that class
  if (elements.length > 0) return elements[0];

  return null;
}

// Fallback: find element by matching page_content text against article/section containers
function findContentByText(doc, textToHighlight) {
  if (!textToHighlight) return null;

  const normalizedSearch = normalizeText(textToHighlight);
  const searchWords = normalizedSearch.split(' ').filter(w => w.length > 2);
  if (searchWords.length === 0) return null;

  // Check article-content elements first (most specific)
  const containers = doc.querySelectorAll('.article-content, .article-paragraph, .highlightable');
  for (const container of containers) {
    const text = normalizeText(container.textContent);
    if (text.includes(normalizedSearch.substring(0, 80))) {
      return container.closest('.article') || container;
    }
  }

  // Word-based scoring across articles
  const articles = doc.querySelectorAll('.article');
  let bestMatch = null;
  let bestScore = 0;

  articles.forEach(article => {
    const articleText = normalizeText(article.textContent);
    let score = 0;
    let totalWeight = 0;

    searchWords.forEach(word => {
      const weight = Math.min(word.length / 10, 1);
      totalWeight += weight;
      if (articleText.includes(word)) score += weight;
    });

    const ratio = totalWeight > 0 ? score / totalWeight : 0;
    if (ratio > bestScore && ratio > 0.4) {
      bestScore = ratio;
      bestMatch = article;
    }
  });

  if (bestMatch) {
    return bestMatch.querySelector('.article-content') || bestMatch;
  }

  return null;
}

// Main export: highlight an HTML citation in a legal document
export function highlightHtmlText(textToHighlight, iframeDocument, _language = 'ar', color = DocumentColorEnum.yellow, citation = null, { skipScroll = false } = {}) {
  if (!iframeDocument || !textToHighlight) return null;

  addHighlightStyles(iframeDocument);
  removeExistingHighlights(iframeDocument);

  const highlightColor = highlightColors[color] || highlightColors[DocumentColorEnum.yellow];
  const sectionTitle = citation?.metadata?.section_title;
  const docType = citation?.metadata?.doc_type?.toLowerCase();

  // "document" means a doc-level citation — just show the doc, don't highlight
  if (sectionTitle === 'document') {
    return null;
  }

  let elementToHighlight = null;
  let strategy = '';

  // Strategy 1: Jurisprudence/Case Law — find section by key_number pattern
  // Backend uses 'case-law', but also check 'jurisprudence' for backwards compatibility
  if (docType === 'case-law' || docType === 'jurisprudence') {
    const parsed = parseJurisprudenceSectionTitle(sectionTitle);
    if (parsed) {
      elementToHighlight = findUaeJurisprudenceSection(iframeDocument, parsed.key, parsed.number);
      if (elementToHighlight) strategy = `case-law: ${parsed.key}_${parsed.number}`;
    }
  }

  // Strategy 2: Law — find article by data-article attribute
  if (!elementToHighlight && sectionTitle) {
    elementToHighlight = findUaeLawArticle(iframeDocument, sectionTitle);
    if (elementToHighlight) strategy = `law article: data-article match for "${sectionTitle}"`;
  }

  // Strategy 3: Fallback — text content matching
  if (!elementToHighlight) {
    elementToHighlight = findContentByText(iframeDocument, textToHighlight);
    if (elementToHighlight) strategy = 'text content fallback';
  }

  if (elementToHighlight) {
    highlightElement(elementToHighlight, highlightColor);
    console.log(`[highlight] ${strategy}`, elementToHighlight);
    if (!skipScroll) scrollToElementOnce(elementToHighlight);
    return elementToHighlight;
  }

  console.warn('[highlight] No element found', {
    docType,
    sectionTitle,
    textPreview: textToHighlight?.substring(0, 80)
  });
  return null;
}

/**
 * Find the chunk index that contains the target element.
 * Searches through the parsed chunks' HTML to find the matching article/section.
 *
 * @param {Array} chunks - Array of { id, html, articleNumber, index }
 * @param {string} textToHighlight - The citation text
 * @param {object} citation - The citation object with metadata
 * @returns {number} chunk index, or -1 if not found
 */
export function findTargetChunkIndex(chunks, textToHighlight, citation) {
  if (!chunks || chunks.length === 0) return -1;

  const sectionTitle = citation?.metadata?.section_title;
  const docType = citation?.metadata?.doc_type?.toLowerCase();

  // Doc-level citation — no specific chunk to target
  if (sectionTitle === 'document') return -1;

  // Strategy 1: For law articles — find by article number from section_title
  if (sectionTitle) {
    const numberMatch = sectionTitle.match(/\d+(?:[-./]\d+)*/);
    if (numberMatch) {
      const articleNumber = numberMatch[0];
      const idx = chunks.findIndex(c => c.articleNumber === articleNumber);
      if (idx >= 0) return idx;
    }
  }

  // Strategy 2: For jurisprudence — find by section class in chunk HTML
  if (docType === 'case-law' || docType === 'jurisprudence') {
    const parsed = parseJurisprudenceSectionTitle(sectionTitle);
    if (parsed) {
      const cssClass = parsed.key.replace(/_/g, '-');
      const idx = chunks.findIndex(c => c.html.includes(`class="${cssClass}`) || c.html.includes(`class="`) && c.html.includes(cssClass));
      if (idx >= 0) return idx;
    }
  }

  // Strategy 3: Text content matching across chunks
  if (textToHighlight) {
    const normalizedSearch = normalizeText(textToHighlight);
    const searchSnippet = normalizedSearch.substring(0, 80);

    if (searchSnippet) {
      const parser = new DOMParser();
      let bestIdx = -1;
      let bestScore = 0;

      const searchWords = normalizedSearch.split(' ').filter(w => w.length > 2);

      for (let i = 0; i < chunks.length; i++) {
        const doc = parser.parseFromString(`<div>${chunks[i].html}</div>`, 'text/html');
        const chunkText = normalizeText(doc.body.textContent || '');

        // Direct substring match
        if (chunkText.includes(searchSnippet)) return i;

        // Word-based scoring
        if (searchWords.length > 0) {
          let score = 0;
          let totalWeight = 0;
          searchWords.forEach(word => {
            const weight = Math.min(word.length / 10, 1);
            totalWeight += weight;
            if (chunkText.includes(word)) score += weight;
          });
          const ratio = totalWeight > 0 ? score / totalWeight : 0;
          if (ratio > bestScore && ratio > 0.4) {
            bestScore = ratio;
            bestIdx = i;
          }
        }
      }

      if (bestIdx >= 0) return bestIdx;
    }
  }

  return -1;
}

/**
 * Highlight a citation within the virtualized HTML container's rendered DOM.
 * Call this AFTER the target chunk has been scrolled into view and rendered.
 *
 * @param {string} textToHighlight - The citation text
 * @param {HTMLElement} containerEl - The virtualized wrapper DOM element (or outerRef)
 * @param {string} _language - Language code
 * @param {string} color - Color key from DocumentColorEnum
 * @param {object} citation - The citation object
 * @returns {HTMLElement|null} The highlighted element
 */
export function highlightInVirtualizedContainer(textToHighlight, containerEl, _language = 'ar', color = DocumentColorEnum.yellow, citation = null) {
  if (!containerEl || !textToHighlight) return null;

  removeExistingHighlights(containerEl);

  const highlightColor = highlightColors[color] || highlightColors[DocumentColorEnum.yellow];
  const sectionTitle = citation?.metadata?.section_title;
  const docType = citation?.metadata?.doc_type?.toLowerCase();

  let elementToHighlight = null;
  let strategy = '';

  // Strategy 1: Jurisprudence section
  if (docType === 'case-law' || docType === 'jurisprudence') {
    const parsed = parseJurisprudenceSectionTitle(sectionTitle);
    if (parsed) {
      elementToHighlight = findUaeJurisprudenceSection(containerEl, parsed.key, parsed.number);
      if (elementToHighlight) strategy = `case-law: ${parsed.key}_${parsed.number}`;
    }
  }

  // Strategy 2: Law article by data-article
  if (!elementToHighlight && sectionTitle) {
    elementToHighlight = findUaeLawArticle(containerEl, sectionTitle);
    if (elementToHighlight) strategy = `law article: data-article match for "${sectionTitle}"`;
  }

  // Strategy 3: Text content fallback
  if (!elementToHighlight) {
    elementToHighlight = findContentByText(containerEl, textToHighlight);
    if (elementToHighlight) strategy = 'text content fallback';
  }

  if (elementToHighlight) {
    highlightElement(elementToHighlight, highlightColor);
    console.log(`[highlight-virtualized] ${strategy}`, elementToHighlight);
    return elementToHighlight;
  }

  console.warn('[highlight-virtualized] No element found in rendered chunks', {
    docType,
    sectionTitle,
    textPreview: textToHighlight?.substring(0, 80)
  });
  return null;
}

export function resetScrollState() {
  isAutoScrolling = false;
  if (autoScrollTimer) {
    clearTimeout(autoScrollTimer);
    autoScrollTimer = null;
  }
}
